import * as React from "react";
import Box from "@mui/material/Box";
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiRef,
  GridCellModes,
} from "@mui/x-data-grid-pro";
import * as _ from "lodash";
import { getWeightInGrams, getWeightInUnitFromGrams } from "../utils";
import { Backpack, ExternalLink, Shirt, Utensils } from "lucide-react";
import WEIGHT_TYPES from "../constants/weightTypes.json";
import ShareDetailPanelContent from "./ShareDetailPanelContent";
import CustomDetailPanelToggle from "./CustomDetailPanelToggle";
import {
  Stack,
  Tooltip,
  Typography,
  Link,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { getExternalUrl, logLinkClick } from "../utils/links";
import { useMediaQuery } from "react-responsive";
import {
  getColorWithMode,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import { isValidUrl } from "../utils/validationUtils";
import ItemImage from "./ItemImage";

const UNEDITABLE_ROW_TYPES = ["ADD_ROW", "ADDING_ROW", "DIVIDER"];

const INITIAL_SUMMARY_ROW = {
  id: "SUMMARY",
  type: "DIVIDER",
  item: "",
  weight: 0,
  unit: "oz",
  price: 0,
};

const calculateSummaryRow = (rows, summaryRowUnit) => {
  const summaryValues = Object.values(rows).reduce(
    (acc, curr) => {
      const weightInGrams = getWeightInGrams(curr.weight, curr.unit);
      const rowWeight = weightInGrams * curr.quantity;
      return {
        weight: acc.weight + rowWeight,
        price: acc.price + curr.price * curr.quantity,
      };
    },
    {
      weight: 0,
      price: 0,
    }
  );
  const finalValues = {
    weight: getWeightInUnitFromGrams(summaryValues.weight, summaryRowUnit),
    price: summaryValues.price,
    unit: summaryRowUnit,
  };
  return { ...INITIAL_SUMMARY_ROW, ...finalValues };
};

const ShareDataGridContainer = ({
  tableRows,
  handleSetTableData,
  tableIndex,
  tableId,
  summaryRowUnit,
  affiliateLinksAreOff,
  isDarkMode,
  overridingUnit,
  packCurrency,
  hidePrices,
  handleChangeAllUnits,
  showImages,
}) => {
  const isMobile = useMediaQuery({ maxWidth: "898px" });
  const summaryRow = calculateSummaryRow(tableRows, summaryRowUnit);
  const [cellModesModel, setCellModesModel] = React.useState({});
  const apiRef = useGridApiRef();

  const pinnedRows = {
    bottom: [summaryRow],
  };

  const getUnitFromOverridingUnit = (weight) => {
    if (overridingUnit === "METRIC") {
      return weight < 1000 ? "g" : "kg";
    } else {
      return weight < 454 ? "oz" : "lb";
    }
  };

  const handleSetNewOverridingUnit = () => {
    if (!overridingUnit) return;
    const summaryRowUnit = overridingUnit === "METRIC" ? "kg" : "lb";
    handleSetTableData(tableIndex, "summaryRowUnit", summaryRowUnit);
    // this has to have been unit
    const newRows = tableRows;
    Object.keys(newRows).forEach((id) => {
      const oldWeight = newRows[id].weight;
      const oldUnit = newRows[id].unit;

      const newWeightInGrams = getWeightInGrams(oldWeight, oldUnit);
      const newUnit = getUnitFromOverridingUnit(newWeightInGrams);
      const newWeightInUnit = getWeightInUnitFromGrams(
        newWeightInGrams,
        newUnit
      );
      newRows[id].weight = newWeightInUnit;
      newRows[id].unit = newUnit;
    });

    handleSetTableData(tableIndex, "rows", newRows);
  };

  React.useEffect(() => {
    handleSetNewOverridingUnit();
  }, [overridingUnit]);

  const getIconColor = (isSelected) => {
    return isSelected ? "#00a2e4" : "grey";
  };

  const handleCellClick = React.useCallback((params, event) => {
    if (!params.isEditable) {
      if (params.field !== "__detail_panel_toggle__") {
        apiRef.current.toggleDetailPanel(params.id);
      }
      return;
    }
    // Ignore portal
    if (!event.currentTarget.contains(event.target)) {
      return;
    }

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = React.useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const columns = [
    ...(showImages
      ? [
          {
            field: "rowImage",
            headerName: "",
            width: isMobile ? 56 : 50,
            editable: false,
            sortable: false,
            filterable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
              const imageUrl =
                typeof params.row.imageUrl === "string"
                  ? params.row.imageUrl.trim()
                  : "";
              if (UNEDITABLE_ROW_TYPES.includes(params.row.type) || !imageUrl) {
                return <></>;
              }
              return (
                <ItemImage
                  src={imageUrl}
                  alt={params.row.item || ""}
                  iconSize={16}
                  style={{
                    width: isMobile ? "40px" : "30px",
                    height: isMobile ? "40px" : "30px",
                    objectFit: "contain",
                    borderRadius: "4px",
                  }}
                />
              );
            },
          },
        ]
      : []),
    {
      field: "item",
      headerName: "Item",
      width: isMobile ? 235 : 285,
      editable: false,
      flex: 1,
      headerAlign: "left",
      colSpan: ({ row }) => {
        if (row.type === "DIVIDER") {
          return 2;
        }
        if (row.type === "ADD_ROW" || row.type === "ADDING_ROW") {
          return 7;
        }
        if (isMobile) {
          return 2;
        }
      },
      sortable: false,
      headerClassName: isMobile
        ? "lastcolumnSeparator"
        : "firstcolumnSeparator",
      renderCell: (params) => (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ width: "100%" }}
          justifyContent="space-between"
        >
          {isMobile ? (
            <Stack sx={{ width: "calc(100% - 25px)" }}>
              <Typography
                variant="h6"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingBottom: "0px",
                }}
              >
                {params.row.item}
              </Typography>
              <Typography
                variant={"body"}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: "-5px",
                  color: getColorWithMode(isDarkMode, "lightShadow"),
                }}
              >
                {params.row.category}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant={isMobile ? "h6" : "body2"}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row.item}
            </Typography>
          )}
          {isValidUrl(params.row.url) ? (
            <Link
              type="button"
              size="small"
              sx={{
                color: getColorWithMode(isDarkMode, "lightText"),
                "&:hover": { opacity: "0.6", cursor: "pointer" },
              }}
              target="_blank"
              href={getExternalUrl(params.row.url, affiliateLinksAreOff)}
              onClick={() => {
                logLinkClick(params.row.url);
              }}
            >
              <ExternalLink size={18} />
            </Link>
          ) : (
            <></>
          )}
        </Stack>
      ),
    },
    {
      field: "category",
      headerName: isMobile ? "" : "Item Type",
      editable: false,
      headerAlign: "left",
      sortable: false,
      headerClassName: isMobile ? "firstcolumnSeparator" : "",
      flex: 0.5,
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      width: 60,
      editable: false,
      headerAlign: "right",
      sortable: false,
    },
    {
      field: "weight",
      headerName: "Weight",
      type: "number",
      width: 85,
      editable: false,
      headerAlign: "right",
      sortable: false,
      headerClassName: "lastcolumnSeparator",
      renderCell: (params) => {
        return typeof params.row.weight === "number"
          ? params.row.weight.toFixed(params.row.unit === "g" ? 0 : 1)
          : params.row.weight;
      },
    },
    {
      field: "unit",
      headerName: "",
      width: 67,
      height: "36px",
      valueOptions: ["g", "oz", "lb", "kg"],
      editable: true,
      headerAlign: "left",
      sortable: false,
      renderCell: (params) => (
        <FormControl variant="standard">
          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={params.row.unit}
            onChange={(e) => {
              if (params.row.id === "SUMMARY") {
                handleSetTableData(
                  tableIndex,
                  "summaryRowUnit",
                  e.target.value
                );
              } else {
                handleChangeAllUnits(e.target.value);
              }
            }}
          >
            <MenuItem value="g">g</MenuItem>
            <MenuItem value="oz">oz</MenuItem>
            <MenuItem value="lb">lb</MenuItem>
            <MenuItem value="kg">kg</MenuItem>
          </Select>
        </FormControl>
      ),
      cellClassName: "selectCell",
    },
    ...(hidePrices
      ? []
      : [
          {
            field: "price",
            headerName: "Price",
            width: 85,
            type: "number",
            editable: false,
            headerAlign: "center",
            sortable: false,
            headerClassName: "lastcolumnSeparator",
            renderCell: (params) => {
              if (params.row.id === "SUMMARY") {
                try {
                  return Number(params.value).toLocaleString("en-US", {
                    style: "currency",
                    currency: packCurrency,
                  });
                } catch (e) {
                  return Number(params.value).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  });
                }
              } else {
                return Number(params.value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                });
              }
            },
          },
        ]),
    {
      field: "action",
      headerName: "",
      width: 20,
      headerClassName: "lastcolumnSeparator",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (UNEDITABLE_ROW_TYPES.includes(params.row.type)) {
          return <></>;
        }
        if (params.row.weightType === WEIGHT_TYPES.WORN) {
          return (
            <Tooltip
              title={
                tableRows[params.row.id].weightType === WEIGHT_TYPES.WORN
                  ? "This item is marked as worn"
                  : ""
              }
            >
              <Shirt
                size={isMobile ? 20 : 18}
                style={{ marginRight: "7px" }}
                color={getIconColor(
                  tableRows[params.row.id].weightType === WEIGHT_TYPES.WORN
                )}
              />
            </Tooltip>
          );
        }
        if (params.row.weightType === WEIGHT_TYPES.CONSUMABLE) {
          return (
            <Tooltip
              title={
                tableRows[params.row.id].weightType === WEIGHT_TYPES.CONSUMABLE
                  ? "This item is marked as a consumable"
                  : ""
              }
            >
              <Utensils
                size={isMobile ? 20 : 18}
                color={getIconColor(
                  tableRows[params.row.id].weightType ===
                    WEIGHT_TYPES.CONSUMABLE
                )}
              />
            </Tooltip>
          );
        }
        return (
          <>
            <Tooltip title={"This item is marked as Base Weight"}>
              <Backpack
                size={isMobile ? 20 : 18}
                color={getIconColor(false)}
              />
            </Tooltip>
          </>
        );
      },
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      headerClassName: "lastcolumnSeparator",
      headerAlign: "right",
      width: isMobile ? 65 : "",
      renderCell: (params) => {
        if (UNEDITABLE_ROW_TYPES.includes(params.row.type)) {
          return <></>;
        }
        return <CustomDetailPanelToggle id={params.id} value={params.value} />;
      },
    },
  ];

  const handleRowUpdate = (cellData) => {
    // recalculate summary row with new unit if that is the update
    const { id } = cellData;
    if (id === "SUMMARY") {
      handleSetTableData(tableIndex, "summaryRowUnit", cellData.unit);
    } else {
      // this has to have been unit
      const newRows = tableRows;
      const oldWeight = newRows[id].weight;
      const oldUnit = newRows[id].unit;

      const newWeightInGrams = getWeightInGrams(oldWeight, oldUnit);
      const newUnit = cellData.unit;
      const newWeightInUnit = getWeightInUnitFromGrams(
        newWeightInGrams,
        newUnit
      );
      newRows[id].weight = newWeightInUnit;
      newRows[id].unit = newUnit;
      handleSetTableData(tableIndex, "rows", newRows);
    }
    return cellData;
  };

  const handleRowEditError = (err) => {
    console.log(err);
  };

  const getDetailPanelHeight = React.useCallback(() => 380, []);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "calc(100vw - 35px)",
        "& .MuiDataGrid-columnHeaders": {
          display: tableIndex === 0 ? "" : "none !important",
        },
      }}
      key={`${tableId}-${tableIndex}`}
    >
      <DataGridPro
        apiRef={apiRef}
        sx={{
          borderRadius: "12px",
          borderColor: getBorder(isDarkMode),
          marginTop: "-10px",
          pr: 0,
          "& .MuiDataGrid-columnHeaderTitle": {
            fontFamily: HEADING_FONT,
            fontWeight: 600,
            fontSize: isMobile ? "13px" : "11px",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: getMutedText(isDarkMode),
          },
          "& .MuiDataGrid-columnHeaders": {
            borderColor: getBorder(isDarkMode),
          },
          "& .MuiDataGrid-cell": {
            borderColor: getBorder(isDarkMode),
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: isDarkMode
              ? "rgba(144,202,249,0.06)"
              : "rgba(25,118,210,0.04)",
          },
          "& .MuiDataGrid-pinnedRows": {
            boxShadow: "none",
          },
        }}
        autoHeight
        rows={Object.values(tableRows)}
        pinnedRows={pinnedRows}
        columns={columns}
        disableSelectionOnClick
        hideFooter
        experimentalFeatures={{ newEditingApi: true, rowPinning: true }}
        isCellEditable={(params) => params.colDef.field === "unit"}
        getRowHeight={({ id, densityFactor }) => {
          if (id === "SUMMARY") {
            return 40 * densityFactor;
          }
          // Slightly taller rows when the image column is on, so 30px
          // thumbnails aren't clipped by compact density (~27px rows).
          return (showImages ? 52 : 38) * densityFactor;
        }}
        components={{
          NoRowsOverlay: () => (
            <Box
              sx={{
                textAlign: "center",
                verticalAlign: "center",
                lineHeight: "72px",
                color: "#999",
              }}
            >
              No Items
            </Box>
          ),
        }}
        density={isMobile ? "comfortable" : "compact"}
        processRowUpdate={handleRowUpdate}
        onProcessRowUpdateError={handleRowEditError}
        disableColumnReorder
        disableColumnPinning
        getDetailPanelHeight={getDetailPanelHeight}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        onCellClick={handleCellClick}
        headerHeight={tableIndex === 0 ? 36 : 5}
        getDetailPanelContent={(props) => (
          <ShareDetailPanelContent
            {...props}
            tableIndex={tableIndex}
            handleSetTableData={handleSetTableData}
            tableRows={tableRows}
            tableId={tableId}
            affiliateLinksAreOff={affiliateLinksAreOff}
            isDarkMode={isDarkMode}
          />
        )}
        disableColumnMenu
      />
    </Box>
  );
};

const areEqual = (prevProps, nextProps) => _.isEqual(prevProps, nextProps);

export default React.memo(ShareDataGridContainer, areEqual);
